// src/api/axios.ts
import type { UnknownAction } from "@reduxjs/toolkit";
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import toast from "react-hot-toast";

import { patchStoredAuthSession } from "@/auth/authSessionStorage";
import type { User } from "@/auth/authSlice";
import { getApiBaseUrl, getApiTimeout } from "@/config/appConfig";
import { emitSessionExpired } from "@/services/apiErrorEvents";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * HTTP statuses this request's caller turns into its own UI state. A response with one of them is not retried
     * and raises no global toast. 401 is never affected: the token refresh below runs as for any request.
     */
    handledStatuses?: readonly number[];
    /** True when the caller shows its own state for a failure with no response (network error, timeout). */
    handlesNetworkErrors?: boolean;
  }
}

/**
 * Production-ready axios instance with:
 *  - bearer token attach
 *  - refresh token flow (single refresh request, queue pending requests)
 *  - exponential retry for idempotent requests (GET / HEAD / OPTIONS)
 *  - sensible headers
 *
 * IMPORTANT: adapt the refreshTokenRequest and how you persist tokens to your Redux shape/actions.
 */

// Centralized API configuration
const API_BASE = getApiBaseUrl();
const TIMEOUT = getApiTimeout();

type AuthStateLike = {
  auth?: {
    token?: string;
    accessToken?: string;
    user?: {
      accessToken?: string;
    };
  };
};

type HttpErrorLike = {
  config?: AxiosRequestConfig;
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
};

type RetryableRequestConfig = AxiosRequestConfig & {
  __retryCount?: number;
  _retry?: boolean;
};

type TokenPayload = {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  token?: string;
};

type ReduxStoreLike = {
  getState: () => unknown;
  dispatch: (action: UnknownAction) => unknown;
};

type ApiEnvelope<T> = {
  data?: T;
};

type QueryClientLike = {
  removeQueries: () => void;
};

let reduxStore: ReduxStoreLike | null = null;
let queryClient: QueryClientLike | null = null;

function isTokenExpiredByStorage(): boolean {
  const expiryRaw = localStorage.getItem("tokenExpiry");
  if (!expiryRaw) return false;
  const expiry = Number(expiryRaw);
  if (!Number.isFinite(expiry)) return false;
  const now = Math.floor(Date.now() / 1000);
  return now >= expiry;
}

/** Exported for non-axios consumers that need the same token/expiry logic (e.g. SignalR). */
export function getCurrentAccessToken() {
  const auth = getAuthState();
  const stateToken = auth?.accessToken ?? auth?.token ?? auth?.user?.accessToken;
  const storedToken = localStorage.getItem("accessToken") ?? undefined;
  const token = stateToken ?? storedToken;
  if (!token) return undefined;
  if (isTokenExpiredByStorage()) return undefined;
  return token;
}

function isAuthEndpoint(config?: AxiosRequestConfig) {
  const path = normalizePath(config?.url, config?.baseURL).toLowerCase();
  return (
    path.startsWith("/auth/otp/send") ||
    path.startsWith("/auth/otp/verify") ||
    path.startsWith("/auth/session/activate") ||
    path.startsWith("/auth/otp/refresh") ||
    path.startsWith("/auth/logout")
  );
}

// Endpoints whose failures are expected/handled by the caller and should
// never surface a global error toast (e.g. an unmapped host resolving to
// "no tenant" is normal, not an error worth alarming a pre-login user with).
function isSilentEndpoint(config?: AxiosRequestConfig) {
  const path = normalizePath(config?.url, config?.baseURL).toLowerCase();
  return path.endsWith("/tenants/resolve");
}

/**
 * Optional injection point to avoid circular deps:
 * api/axios.ts must not import redux/store.ts directly.
 */
export function setReduxStore(store: ReduxStoreLike) {
  reduxStore = store;
}

/**
 * Same injection point, for the React Query client. The forced support-session exit below
 * is a third tenant-switch path (alongside usePlatformTenantSession's start/end) and must
 * drop the tenant-scoped cache too — most query keys carry no tenant id, so without this
 * the previous tenant's data survives the switch back to the Platform Owner.
 */
export function setQueryClient(client: QueryClientLike) {
  queryClient = client;
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  // Allow self-signed certificates in development
  ...(import.meta.env.DEV && {
    httpsAgent: {
      rejectUnauthorized: false,
    } as unknown,
  }),
  // Optionally transform responses here
  // transformResponse: [...axios.defaults.transformResponse, (data) => { ... }]
});

const STATUS_FALLBACKS: Record<number, { title: string; message: string }> = {
  400: {
    title: "Invalid request",
    message: "Please check your input and try again.",
  },
  401: {
    title: "Session expired",
    message: "Your session has expired. Please sign in again.",
  },
  403: {
    title: "Not authorized",
    message: "You do not have permission to perform this action.",
  },
  404: {
    title: "Not found",
    message: "We could not find what you are looking for.",
  },
  409: {
    title: "Conflict",
    message: "This action conflicts with existing data. Please refresh and try again.",
  },
  422: {
    title: "Validation issue",
    message: "Please check your input and try again.",
  },
};

const DEFAULT_SERVER_ERROR = {
  title: "Something went wrong",
  message: "Please try again in a moment.",
};

const ERROR_OVERRIDES: Array<{
  method: string;
  path: string;
  title: string;
  message: string;
}> = [
  {
    method: "post",
    path: "/sections",
    title: "Could not create class",
    message:
      "Something went wrong while creating the section. Please try again in a moment or contact support.",
  },
];

function normalizePath(url?: string, baseURL?: string) {
  if (!url) return "";
  const rawUrl = url.startsWith("http") ? url : `${baseURL ?? ""}${url}`;
  try {
    const parsed = new URL(rawUrl, baseURL ?? "http://localhost");
    return parsed.pathname || url;
  } catch {
    return url.startsWith("/") ? url : `/${url}`;
  }
}

function isSafeBackendMessage(message: string) {
  const trimmed = message.trim();
  if (!trimmed) return false;
  if (trimmed.length > 180) return false;
  const lower = trimmed.toLowerCase();
  const unsafePatterns = [
    "exception",
    "stack trace",
    "stacktrace",
    "system.",
    "microsoft.",
    "linq",
    "dbset",
    "entityframework",
    "sql",
    "npgsql",
    "see https://",
    " at ",
    "()",
  ];
  return !unsafePatterns.some((pattern) => lower.includes(pattern));
}

function getBackendMessage(error: unknown) {
  const data = (error as HttpErrorLike)?.response?.data as
    | {
        message?: string;
        meta?: { message?: string };
        error?: { message?: string };
        errors?: Array<{ message?: string }>;
      }
    | string
    | undefined;
  let directMessage: string | undefined;
  if (typeof data === "string") {
    directMessage = data;
  } else {
    directMessage =
      data?.message ||
      data?.meta?.message ||
      data?.error?.message ||
      data?.errors?.[0]?.message;
  }
  if (typeof directMessage === "string" && isSafeBackendMessage(directMessage)) {
    return directMessage.trim();
  }
  return undefined;
}

function getOverride(config?: AxiosRequestConfig) {
  if (!config) return undefined;
  const method = (config.method ?? "get").toLowerCase();
  const path = normalizePath(config.url, config.baseURL);
  return ERROR_OVERRIDES.find(
    (override) => override.method === method && override.path === path,
  );
}

function getFallbackCopy(status?: number) {
  if (!status) return DEFAULT_SERVER_ERROR;
  if (STATUS_FALLBACKS[status]) return STATUS_FALLBACKS[status];
  if (status >= 500) return DEFAULT_SERVER_ERROR;
  return { title: "Request failed", message: "Please try again." };
}

function showToastError(title: string, message: string, toastId?: string) {
  const composed = title ? `${title}: ${message}` : message;
  toast.error(composed, { id: toastId });
}

/* -------------------------
  Helper: get tokens from store
   - Adapt if your auth state stores tokens differently
---------------------------*/
function getAuthState() {
  try {
    const state = reduxStore?.getState() as AuthStateLike;
    return state?.auth ?? null;
  } catch {
    return null;
  }
}

/* -------------------------
  Attach bearer token (if available)
---------------------------*/
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    try {
      const token = getCurrentAccessToken();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
      }
    } catch {
      //
    }

    // File uploads: the instance default `Content-Type: application/json` above would otherwise
    // destroy the payload. Axios 1.x checks the content type BEFORE its adapter runs
    // (transformRequest: `isFormData ? (hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data)`),
    // so a JSON content type makes it serialize the FormData to JSON — every File collapses to `{}`
    // and the request goes out as application/json with no parts at all. ASP.NET's [FromForm]
    // binding then sees no form and reports the file fields as missing/required.
    // Clearing the header lets the FormData through; the browser sets multipart/form-data plus the
    // boundary itself (a hand-set "multipart/form-data" without a boundary would be unparseable).
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers?.delete?.("Content-Type");
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/* -------------------------
  Simple exponential retry for idempotent requests
  - Only retries GET/HEAD/OPTIONS by default
  - Uses attempt count stored on config.__retryCount
---------------------------*/
const MAX_RETRIES = 3;
const RETRYABLE_METHODS = new Set(["get", "head", "options"]);

// Only statuses that plausibly differ on a second try. A plain 500 is the server telling us it
// broke on this exact request; repeating it just multiplies the failure — and React Query retries
// on top of this, so a blanket `status >= 500` turned one failing query into eight requests and
// made an ordinary bug look like a request loop in the Network tab.
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

function isRetryableFailure(status: number | undefined) {
  // No status at all means no response: a network drop or a timeout, both worth retrying.
  return status === undefined ? true : RETRYABLE_STATUSES.has(status);
}

/** True when the request's caller turns this HTTP status into its own UI state (`handledStatuses`). Never 401. */
function isStatusHandledByCaller(
  config: AxiosRequestConfig | undefined,
  status: number | undefined,
) {
  if (status === undefined || status === 401) return false;
  return config?.handledStatuses?.includes(status) ?? false;
}

/** True when the request's caller reports this failure itself, so no global toast is shown for it. */
function isFailureHandledByCaller(
  config: AxiosRequestConfig | undefined,
  status: number | undefined,
) {
  if (status === undefined) return config?.handlesNetworkErrors === true;
  return isStatusHandledByCaller(config, status);
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: unknown) => {
    const errorLike = error as HttpErrorLike;
    const config = errorLike?.config as RetryableRequestConfig | undefined;

    // Network or timeout errors may not have response
    const status = errorLike?.response?.status;

    // Retry logic for idempotent requests (simple backoff)
    if (
      config &&
      RETRYABLE_METHODS.has((config.method ?? "").toLowerCase()) &&
      isRetryableFailure(status) &&
      !isStatusHandledByCaller(config, status)
    ) {
      config.__retryCount = config.__retryCount ?? 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        const delay = Math.pow(2, config.__retryCount) * 100; // 200ms, 400ms, 800ms...
        await new Promise((res) => setTimeout(res, delay));
        return api.request(config);
      }
    }

    return Promise.reject(errorLike);
  },
);

/* -------------------------
  Refresh token flow
  - Ensures only one refresh request runs at a time.
  - Queues requests that arrived during refresh and replays them.
  - IMPORTANT: adapt refreshTokenRequest() to your backend.
---------------------------*/
let isRefreshing = false;
let refreshQueue: {
  resolve: (token?: string) => void;
  reject: (error: unknown) => void;
}[] = [];

/** Put your refresh token call here. This should return new tokens or throw. */
async function refreshTokenRequest() {
  // Get tokens from localStorage instead of store
  const refreshToken = localStorage.getItem("refreshToken");
  const deviceId = localStorage.getItem("device_identifier");
  const userId = localStorage.getItem("userId");

  if (!refreshToken) throw new Error("No refresh token available");
  if (!deviceId) throw new Error("No device identifier available");
  if (!userId) throw new Error("No user id available");

  // Do not use the api instance (it has interceptors) to avoid loops; use a fresh axios.
  const plain = axios.create({ baseURL: API_BASE, timeout: TIMEOUT });
  const resp = await plain.post("/auth/otp/refresh", {
    refreshToken,
    deviceIdentifier: deviceId,
    userId,
  });

  const payload = resp.data as ApiEnvelope<unknown> | unknown;
  return payload && typeof payload === "object" && "data" in payload
    ? (payload as ApiEnvelope<TokenPayload>).data
    : payload;
}

/** Save new tokens into redux store - adapt to your action creators */
function persistNewTokens(tokens: TokenPayload | null | undefined) {
  try {
    if (tokens?.accessToken) localStorage.setItem("accessToken", tokens.accessToken);
    if (tokens?.refreshToken) localStorage.setItem("refreshToken", tokens.refreshToken);

    // Re-calculate expiry from expiresIn (seconds)
    if (tokens?.expiresIn) {
      const newExpiry = Math.floor(Date.now() / 1000) + tokens.expiresIn;
      localStorage.setItem("tokenExpiry", String(newExpiry));
    }

    // Keep Redux + persisted session in sync (so UI reacts).
    if (tokens?.accessToken || tokens?.refreshToken) {
      reduxStore?.dispatch({
        type: "auth/patchUser",
        payload: {
          accessToken: tokens?.accessToken,
          refreshToken: tokens?.refreshToken,
        },
      } as UnknownAction);

      try {
        const raw = localStorage.getItem("auth_session_v1");
        if (raw) {
          const session = JSON.parse(raw);
          const next = {
            ...session,
            user: {
              ...(session.user ?? {}),
              accessToken: tokens?.accessToken ?? session?.user?.accessToken,
              refreshToken: tokens?.refreshToken ?? session?.user?.refreshToken,
            },
            ...(tokens?.expiresIn
              ? { tokenExpiry: String(Math.floor(Date.now() / 1000) + tokens.expiresIn) }
              : {}),
          };
          localStorage.setItem("auth_session_v1", JSON.stringify(next));
        }
      } catch {
        // ignore
      }
    }
  } catch {
    // swallow; or send to monitoring
  }
}

/** Called to process queued requests after refresh finishes */
function processQueue(error: unknown, token?: string) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

/* Response interceptor to handle 401 and queue requests while refreshing */
api.interceptors.response.use(
  (res: AxiosResponse) => res,
  async (err: unknown) => {
    const errorLike = err as HttpErrorLike;
    const maybeOriginal = errorLike?.config as RetryableRequestConfig | undefined;
    const status = errorLike?.response?.status;

    // if config is missing we cannot retry or modify, just forward
    if (!maybeOriginal) {
      return Promise.reject(err);
    }

    const originalRequest: AxiosRequestConfig & { _retry?: boolean } = maybeOriginal;

    // Never attempt token refresh for authentication endpoints.
    if (status === 401 && isAuthEndpoint(originalRequest)) {
      return Promise.reject(errorLike);
    }

    if (errorLike?.code === "ERR_CANCELED") {
      return Promise.reject(errorLike);
    }

    // Caller handles these failures itself — skip retry/refresh/toast entirely.
    if (isSilentEndpoint(originalRequest)) {
      return Promise.reject(errorLike);
    }

    // A Platform Support Session token is a standalone 60-minute credential with no
    // refresh path (see PlatformTenantSessionDto — no refreshToken). The refresh-token
    // flow below would otherwise use the still-present platform-owner refreshToken/userId
    // (deliberately left untouched so Exit can restore them) and silently swap identity
    // back to the platform token without updating platformSession/tenant-scoped UI state.
    //
    // Not every 401 during a support session means the session itself is invalid — some
    // tenant-scoped endpoints require real tenant-role permissions a support session doesn't
    // carry (TenantContextMiddleware.PlatformSupportSession.* is the specific "session is
    // actually gone" signal; other 401s are just this-endpoint-isn't-available). Either way,
    // we must never let the ordinary refresh flow below run here — but we only force an exit
    // back to the platform console when the backend confirms the session itself is dead.
    if (status === 401) {
      const state = reduxStore?.getState() as
        | {
            platformSession?: {
              active?: boolean;
              homeAccessToken?: string | null;
              homeTokenExpiry?: string | null;
              homeUser?: unknown;
            };
          }
        | undefined;

      if (state?.platformSession?.active) {
        const backendErrorCode = (
          errorLike?.response?.data as { errors?: Array<{ code?: string }> } | undefined
        )?.errors?.[0]?.code;
        const isSupportSessionInvalid =
          backendErrorCode === "PlatformSupportSession.Invalid" ||
          backendErrorCode === "PlatformSupportSession.Malformed";

        if (isSupportSessionInvalid) {
          const { homeAccessToken, homeTokenExpiry, homeUser } = state.platformSession;
          if (homeAccessToken) localStorage.setItem("accessToken", homeAccessToken);
          if (homeTokenExpiry) localStorage.setItem("tokenExpiry", homeTokenExpiry);
          // Drop the tenant's cached data before the identity swap below re-renders the app.
          // Most query keys carry no tenant id, so anything left here would be served to the
          // Platform Owner as their own. Runs synchronously ahead of the dispatches, which
          // React batches — so no query can observe the new identity against the old cache.
          queryClient?.removeQueries();
          reduxStore?.dispatch({
            type: "platformSession/exitSupportSession",
          } as UnknownAction);
          if (homeUser) {
            reduxStore?.dispatch({
              type: "auth/setUser",
              payload: homeUser,
            } as UnknownAction);
          }
          // Drop the persisted support session too, not just the in-memory slice — otherwise a
          // refresh after a forced exit rehydrates a session the backend has already rejected.
          patchStoredAuthSession({
            ...(homeUser ? { user: homeUser as User } : {}),
            platformSession: undefined,
          });
          toast.error(
            "Your platform support session ended. You've been returned to the Platform Owner console.",
          );
        }

        return Promise.reject(errorLike);
      }
    }

    // The caller shows its own state for this failure. Never a 401: the refresh flow below still runs for those.
    if (isFailureHandledByCaller(originalRequest, status)) {
      return Promise.reject(errorLike);
    }

    // Handle 403 Forbidden (permission denied) errors
    if (status === 403) {
      const backendMessage = getBackendMessage(errorLike);
      const fallback = getFallbackCopy(status);
      showToastError(fallback.title, backendMessage ?? fallback.message, `api-${status}`);
      return Promise.reject(errorLike);
    }

    // If 401 but no original request, treat as session expired
    if (status === 401 && !originalRequest) {
      const fallback = getFallbackCopy(401);
      emitSessionExpired({ title: fallback.title, message: fallback.message });
      return Promise.reject(errorLike);
    }

    // If not 401, handle globally and reject
    if (status !== 401) {
      const backendMessage = getBackendMessage(errorLike);
      const override = getOverride(originalRequest);
      const fallback = getFallbackCopy(status);
      const message = backendMessage ?? override?.message ?? fallback.message;

      if (status === 409 || status === 400) {
        showToastError("", message, `api-${status ?? "unknown"}`);
        return Promise.reject(errorLike);
      }

      if (status && status >= 500) {
        showToastError(
          DEFAULT_SERVER_ERROR.title,
          DEFAULT_SERVER_ERROR.message,
          `api-${status}`,
        );
        return Promise.reject(errorLike);
      }

      const title = override?.title ?? fallback.title;
      showToastError(title, message, `api-${status ?? "unknown"}`);
      return Promise.reject(errorLike);
    }

    // Prevent retry loop
    if (originalRequest._retry) {
      return Promise.reject(errorLike);
    }

    // Queue this request while refresh is happening
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token?: string) => {
            if (token) {
              // set auth header and retry
              originalRequest.headers = originalRequest.headers ?? {};
              (originalRequest.headers as Record<string, string>)["Authorization"] =
                `Bearer ${token}`;
            }
            originalRequest._retry = true;
            resolve(api.request(originalRequest));
          },
          reject,
        });
      });
    }

    // Not refreshing yet -> start refresh
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const data = (await refreshTokenRequest()) as TokenPayload;
      // Persist tokens to store; adapt shape
      persistNewTokens(data);

      // Extract new access token from response - adapt field
      const newAccessToken = data?.accessToken ?? data?.token ?? undefined;

      processQueue(null, newAccessToken);

      // retry original request with new token
      if (newAccessToken) {
        originalRequest.headers = originalRequest.headers ?? {};
        (originalRequest.headers as Record<string, string>)["Authorization"] =
          `Bearer ${newAccessToken}`;
      }

      return api.request(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, undefined);

      const fallback = getFallbackCopy(401);
      emitSessionExpired({
        title: fallback.title,
        message: fallback.message,
      });

      // Optional: dispatch logout action in store
      try {
        reduxStore?.dispatch({ type: "auth/logout" } as UnknownAction);
      } catch {
        // ignore
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
